import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import {
  FullPatientCalendarEvents,
  CalendarEventType,
  PatientCalendarService,
  FullPatientCalendar,
  CalendarCount,
} from "@/api";
import moment from "moment";

export type PatientCalendarState = {
  calendarEventTypes: CalendarEventType[] | null;
  patientCalendarEvents: FullPatientCalendarEvents | null;
  count: CalendarCount | null;
};

const state: PatientCalendarState = {
  calendarEventTypes: null,
  patientCalendarEvents: null,
  count: null,
};

const getters: GetterTree<PatientCalendarState, RootState> = {
  calendarEventTypes: state => state.calendarEventTypes,
  patientCalendarEvents: state => state.patientCalendarEvents,
  count: state => state.count,
};

const mutations: MutationTree<PatientCalendarState> = {
  SET_COUNT(state, count: CalendarCount) {
    state.count = count;
  },
  SET_CALENDAR_EVENTS(state, events: CalendarEventType[]) {
    state.calendarEventTypes = events;
  },
  SET_PATIENT_CALENDAR_EVENTS(state, events: FullPatientCalendarEvents) {
    state.patientCalendarEvents = events;
  },
  ADD_PATIENT_CALENDAR_EVENT(state, event: FullPatientCalendar) {
    if (!state.patientCalendarEvents) state.patientCalendarEvents = [];
    state.patientCalendarEvents.push(event);
  },
  UPDATE_PATIENT_CALENDAR_EVENT(state, event: FullPatientCalendar) {
    if (!state.patientCalendarEvents) return;
    let index = state.patientCalendarEvents?.findIndex(
      (item: FullPatientCalendar) =>
        item.patientCalendar.id == event.patientCalendar.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.patientCalendarEvents?.splice(index, 1);
      state.patientCalendarEvents?.push(event);
    }
  },
  REMOVE_PATIENT_CALENDAR_EVENT(state, event: FullPatientCalendar) {
    if (!state.patientCalendarEvents) return;
    let index = state.patientCalendarEvents?.findIndex(
      (item: FullPatientCalendar) =>
        item.patientCalendar.id == event.patientCalendar.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.patientCalendarEvents?.splice(index, 1);
    }
  },
};

const actions: ActionTree<PatientCalendarState, RootState> = {
  fetchCount: async ({ commit, rootState }) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const date = moment()
        .local()
        .format("YYYY-MM-DD");
      const count = await PatientCalendarService.getCalendarEventCount({
        companyId,
        date,
      });
      commit("SET_COUNT", count);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchCalendarEventTypes: async (
    { commit, rootState, getters },
    force = false,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    let hasEventTypes = getters["calendarEventTypes"] ? true : false;
    if (force) hasEventTypes = false;
    if (hasEventTypes) return;
    try {
      const types = await PatientCalendarService.getPatientCalendarEventsTypes({
        companyId,
      });
      commit("SET_CALENDAR_EVENTS", types);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  fetchPatientCalendarEvents: async (
    { commit, rootState },
    {
      beginAt,
      endAt,
      force,
    }: { beginAt?: string; endAt?: string; force: boolean },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    let hasEvent = getters["patientCalendarEvents"] ? true : false;
    if (force) hasEvent = false;
    if (hasEvent) return;
    try {
      const events = await PatientCalendarService.getPatientCalendarEvents({
        companyId,
        beginAt,
        endAt,
      });
      commit("SET_PATIENT_CALENDAR_EVENTS", events);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  addPatientCalendarEvent: async (
    { commit, rootState, dispatch },
    requestBody: FullPatientCalendar,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const event = await PatientCalendarService.addPatientCalendarEvent({
        companyId,
        requestBody,
      });
      commit("ADD_PATIENT_CALENDAR_EVENT", event);
      dispatch("fetchCount");
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updatePatientCalendarEvent: async (
    { commit, rootState },
    requestBody: FullPatientCalendar,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const event = await PatientCalendarService.updatePatientCalendarEvent({
        companyId,
        requestBody,
      });
      commit("UPDATE_PATIENT_CALENDAR_EVENT", event);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  deletePatientCalendarEvent: async (
    { commit, rootState },
    requestBody: FullPatientCalendar,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const event = await PatientCalendarService.deletePatientCalendarEvent({
        companyId,
        requestBody,
      });
      commit("REMOVE_PATIENT_CALENDAR_EVENT", event);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
};

export const patientCalendar: Module<PatientCalendarState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
